import {
  Appliance,
  DeviceLike,
  UnitOfMeasurement,
  Group,
  Measurement,
  OptionalTuple,
  DraggableDevice,
} from '@/types/helpers';
import { ItemCreateOpts } from '@/types/stores/project';
import { assertDeprecated, generateRoomPartId, getCurrentTimestamp, simpleClone } from './utils';
import { convertMeasurement, createMeasurement } from '@/common/measurements';
import { convertMeasurementToCm } from './planningUtils';
import Decimal from 'decimal.js';
import {
  Position,
  Rotation,
  Row,
  GroupId,
  RoomId,
  ApplianceId,
  HoodId,
  RowId,
  AppliancePositioner,
  Transform,
} from '@/api/airchef/Api';
import { InvalidStateError } from './errors';

const emptyTransform: Transform = {
  position: {
    x: {
      value: 0,
      type: 'length',
      unit: 'mm',
    },
    y: {
      value: 0,
      type: 'length',
      unit: 'mm',
    },
    z: {
      value: 0,
      type: 'length',
      unit: 'mm',
    },
  },
  size: {
    width: {
      value: 0,
      type: 'length',
      unit: 'mm',
    },
    length: {
      value: 0,
      type: 'length',
      unit: 'mm',
    },
    height: {
      value: 0,
      type: 'length',
      unit: 'mm',
    },
  },
  rotation: {
    x: 0,
    y: 0,
    z: 0,
  },
};

export function createAssetInstance<T extends DraggableDevice>(
  baseAsset: T,
  opts: ItemCreateOpts<T['type']>,
): T | null {
  if (baseAsset.type === 'appliance' && baseAsset.haltonValues.type === 'spacer') {
    console.error('No base spacer to clone');
    return null;
  }
  const newAsset = simpleClone(baseAsset);

  newAsset.id = generateRoomPartId();

  if (!newAsset.airchefValues.transform) {
    newAsset.airchefValues.transform = simpleClone(emptyTransform);
  }
  if (baseAsset.type === 'appliance' || baseAsset.type === 'hood') {
    newAsset.airchefValues.transform.size = simpleClone(
      baseAsset.type === 'appliance' ? baseAsset.haltonValues.size : baseAsset.haltonValues.minSize,
    );
  }

  if (opts.inRoom) {
    newAsset.airchefValues.transform.position.x.unit = 'mm';
    newAsset.airchefValues.transform.position.x.value = opts.x;

    const offset =
      opts.offsetMultiplier !== undefined
        ? convertMeasurement(baseAsset.airchefValues.transform.size.width, 'mm', true)
            .times(opts.offsetMultiplier)
            .toDecimalPlaces(6)
            .toNumber()
        : 0;

    newAsset.airchefValues.transform.position.y.unit = 'mm';
    newAsset.airchefValues.transform.position.y.value = opts.y + offset;

    newAsset.relations.roomId = opts.roomId;

    newAsset.airchefValues.transform.rotation.x = opts.angle ?? 0;

    if (baseAsset.type === 'hood') {
      newAsset.airchefValues.transform.position.z.unit = 'mm';
      newAsset.airchefValues.transform.position.z = convertMeasurement(
        baseAsset.haltonValues.defaultInstallHeight,
        'mm',
      );
    }
  }

  const now = getCurrentTimestamp();
  newAsset.airchefValues.datesAndAudit.createdAt = now;
  newAsset.airchefValues.datesAndAudit.modifiedAt = now;

  return newAsset;
}

export function createSpacer(
  groupId: GroupId,
  roomId: RoomId,
  rowNum: number,
  rowId: RowId,
  width: number,
  length: number,
  angle: number,
  user = '',
): Appliance {
  const now = getCurrentTimestamp();
  return {
    schemaPath: '/schemas/v1/appliance/appliance.schema.json',
    id: generateRoomPartId(),
    type: 'appliance',
    airchefValues: {
      name: 'Spacer',
      datesAndAudit: {
        createdAt: now,
        modifiedAt: now,
        author: user,
        modifiedBy: user,
      },
      transform: {
        position: {
          x: createMeasurement(0, 'cm'),
          y: createMeasurement(0, 'cm'),
          z: createMeasurement(0, 'cm'),
        },
        size: {
          height: createMeasurement(0, 'cm'),
          width: createMeasurement(width, 'cm'),
          length: createMeasurement(length, 'cm'),
        },
        rotation: {
          x: angle,
          y: 0,
          z: 0,
        },
      },
      region: {
        value: {
          value: 'any',
          label: {
            en: 'any',
          },
        },
        enum: [
          {
            value: 'any',
            label: {
              en: 'any',
            },
          },
        ],
      },
      energySourceType: {
        value: {
          value: 'None',
          label: {
            en: 'None',
          },
        },
        enum: [
          {
            value: 'None',
            label: {
              en: 'None',
            },
          },
        ],
      },
      hoodRequiredFlag: false,
    },
    relations: {
      groupId,
      roomId,
      rowId,
      rowNum,
    },
    haltonValues: {
      type: 'spacer',
      id: 0,
      dutyLevel: 0,
    },
  };
}

/**
 * Returns the device offset for the requested axis in cm
 * @param device
 * @param dir
 * @param toDec
 */
export function getDeviceOffset(device: DeviceLike, dir: 'x' | 'y' | 'z', toDec?: false): number;
export function getDeviceOffset(device: DeviceLike, dir: 'x' | 'y' | 'z', toDec: true): Decimal;
export function getDeviceOffset(device: DeviceLike, dir: 'x' | 'y' | 'z', toDec = false): Decimal | number {
  if (device.airchefValues.positionOffset?.[dir]) {
    return convertMeasurementToCm(device.airchefValues.positionOffset[dir], toDec);
  }

  return toDec ? new Decimal(0) : 0;
}

export function createEmptyGroup(projectVer: `v${string}` = 'v1'): Group {
  return {
    id: generateRoomPartId(),
    schemaPath: `/schemas/${projectVer}/group/group.schema.json`,
    type: 'group',
    relations: {
      roomId: '' as RoomId,
      applianceIds: [],
      hoodIds: [],
    },
    rows: [new RowBuilder().build()],
    haltonValues: {
      type: 'group',
      // id: -1,
      // hoodType: '',
      // plenumHeight: 0,
      // frontHeight: 0,
      // skirts: 'NoSkirt',
      // skirtFlag: 'None',
      // exhaust: 0,
      // capture: 0,
      // supply: 0,
      // locationType: 'Island',
      // hasFullBackWall: false,
      // hasAnyBackWall: false,
      // hasRightWall: false,
      // hasLeftWall: false,
      // has2Row: false,
    },
    airchefValues: {
      transform: {
        size: {
          width: createMeasurement(0, 'mm'),
          length: createMeasurement(0, 'mm'),
          height: createMeasurement(0, 'mm'),
        },
        position: {
          x: createMeasurement(0, 'mm'),
          y: createMeasurement(0, 'mm'),
          z: createMeasurement(0, 'mm'),
        },
        rotation: {
          x: 0,
          y: 0,
          z: 0,
        },
      },
      fireCabinets: [],
      kds: {
        hasKds: false,
        width: createMeasurement(0, 'mm'),
        columnLength: createMeasurement(0, 'mm'),
      },
      hasVirtualWall: false,
    },
  };
}

// Group Factory, because why not?
export class GroupUpdater {
  readonly #group: Group;
  #roomId?: RoomId;
  #applianceIds?: ApplianceId[];
  #hoodIds?: HoodId[];
  #position?: Position;
  // #size?: Size;
  #rotation?: Rotation;

  #rows: OptionalTuple<Row> = [];
  #generateId: boolean = false;
  #resetNumber: boolean = false;

  #_rowsSwapped = false;
  get rowsSwapped() {
    return this.#_rowsSwapped;
  }

  constructor(group: Group) {
    this.#group = simpleClone(group);
    this.#rows = simpleClone(this.#group.rows);
  }

  setGenerateId(generate: boolean) {
    this.#generateId = generate;
    return this;
  }

  /**
   * @deprecated
   * @param ids
   * @returns
   */
  pushApplianceId(...ids: ApplianceId[]) {
    assertDeprecated('use rows instead');
    if (!this.#applianceIds) {
      this.#applianceIds = [];
    }
    this.#applianceIds.push(...ids);
    return this;
  }

  /**
   * @deprecated
   * @param ids
   * @returns
   */
  setApplianceIds(ids: ApplianceId[] | ApplianceId) {
    assertDeprecated('use rows instead');
    if (typeof ids === 'string') this.#applianceIds = [ids];
    else this.#applianceIds = [...ids];
    return this;
  }

  /**
   * @deprecated
   * @param ids
   * @returns
   */
  pushHoodIds(...ids: HoodId[]) {
    assertDeprecated('use rows instead');
    if (!this.#hoodIds) {
      this.#hoodIds = [];
    }
    this.#hoodIds.push(...ids);
    return this;
  }

  /**
   * @deprecated
   * @param ids
   * @returns
   */
  setHoodIds(ids: HoodId[] | HoodId) {
    assertDeprecated('use rows instead');
    if (typeof ids === 'string') this.#hoodIds = [ids];
    else this.#hoodIds = [...ids];
    return this;
  }
  setRoomId(roomId: RoomId) {
    this.#roomId = roomId;
    return this;
  }

  setPosition(pos: Position): GroupUpdater;
  setPosition(x: number, y: number, z?: number, unit?: UnitOfMeasurement<'length'>): GroupUpdater;
  setPosition(
    xOrPos: number | Position,
    y: number = 0,
    z: number = 0,
    unit: UnitOfMeasurement<'length'> = 'cm',
  ): GroupUpdater {
    if (typeof xOrPos === 'object') {
      this.#position = simpleClone(xOrPos);
    } else {
      this.#position = {
        x: createMeasurement(xOrPos, unit),
        y: createMeasurement(y, unit),
        z: createMeasurement(z, unit),
      };
    }
    return this;
  }

  setRotation(x: number, y: number = 0, z: number = 0) {
    this.#rotation = {
      x,
      y,
      z,
    };
    return this;
  }

  setRow(index: 0 | 1, action: 'clear'): this;
  setRow(index: 0 | 1, appliances?: ApplianceId[], hoods?: HoodId[]): this;
  setRow(index: 0 | 1, row?: Row): this;
  setRow(index: 0 | 1, appliancesOrRowOrClear?: 'clear' | ApplianceId[] | Row, hoods?: HoodId[]): this {
    if (appliancesOrRowOrClear === 'clear') {
      if (index === 1) delete this.#rows[index];
    } else if (appliancesOrRowOrClear === undefined || Array.isArray(appliancesOrRowOrClear)) {
      const appliances = appliancesOrRowOrClear;

      const builder = new RowBuilder(this.#rows[index]);

      if (appliances !== undefined) {
        builder.setAppliances(appliances);
      }

      if (hoods !== undefined) {
        builder.setHoods(hoods);
      }

      const builtRow = builder.build();
      const rowIsEmpty = builtRow.relations.applianceIds.length < 1 && builtRow.relations.hoodIds.length < 1;

      if (!rowIsEmpty) this.#rows[index] = builder.build();
    } else {
      const newRow = appliancesOrRowOrClear satisfies Row;
      this.#rows[index] = newRow;
    }

    this.#updateGroupIds();

    return this;
  }

  removeAppliance(id: ApplianceId) {
    const rows = this.#rows.map((r, i) => (r !== undefined ? i : undefined)).filter((i) => i !== undefined) as (
      | 0
      | 1
    )[];
    rows.forEach((rowNum) => {
      if (this.#rows[rowNum] === undefined) return;
      this.#rows[rowNum].relations.applianceIds = this.#rows[rowNum].relations.applianceIds.filter((aid) => aid !== id);
      this.#rows[rowNum].relations.floatingApplianceIds = this.#rows[rowNum].relations.floatingApplianceIds.filter(
        (ap) => ap.applianceId !== id,
      );
    });

    this.#cleanupRows();
    this.#updateGroupIds();

    return this;
  }

  removeHood(id: HoodId) {
    const rows = this.#rows.map((r, i) => (r !== undefined ? i : undefined)).filter(Boolean) as (0 | 1)[];
    rows.forEach((rowNum) => {
      if (!this.#rows[rowNum]) return;
      this.#rows[rowNum].relations.hoodIds = this.#rows[rowNum].relations.hoodIds.filter((hid) => hid !== id);
    });

    this.#cleanupRows();
    this.#updateGroupIds();

    return this;
  }

  /**
   * Should the item number be reset for this group?
   * Will be recalculated automatically when put into the data if reset
   * @param reset
   */
  setResetNumber(reset: boolean) {
    this.#resetNumber = reset;
    return this;
  }

  #cleanupRows() {
    const rowsToUpdate: [undefined | Row, undefined | Row] = [undefined, undefined];
    const finalRows = [] as [] | [Row] | [Row, Row];
    this.#rows.forEach((row, index) => {
      if (row === undefined) {
        return;
      }

      if (row.relations.applianceIds.length < 1) {
        rowsToUpdate[index] = undefined;
      } else {
        rowsToUpdate[index] = row;
      }
    });

    if (rowsToUpdate[0] === undefined && rowsToUpdate[1] !== undefined) {
      // We have 2nd row, but not first one, move it to the first slot
      rowsToUpdate[0] = this.#rows[1];
      this.#_rowsSwapped = true;
    }

    rowsToUpdate.forEach((row, i) => {
      if (row !== undefined) {
        finalRows[i] = row;
      }
    });

    if (finalRows.length < 1) {
      throw new InvalidStateError('Group updater ended with 0 rows!');
    }

    this.#rows = finalRows;
  }

  #updateGroupIds() {
    let allApplianceIds = [] as ApplianceId[];
    let allHoodIds = [] as HoodId[];
    for (const row of this.#rows) {
      allApplianceIds = allApplianceIds
        .concat(row?.relations.applianceIds ?? [])
        .concat(row?.relations.floatingApplianceIds.map((ap) => ap.applianceId) ?? []);
      allHoodIds = allHoodIds.concat(row?.relations.hoodIds ?? []);
    }

    this.#applianceIds = Array.from(new Set(allApplianceIds));
    this.#hoodIds = Array.from(new Set(allHoodIds));
  }

  update(): Group {
    const updatedGroup = simpleClone(this.#group);

    if (this.#generateId) {
      updatedGroup.id = generateRoomPartId();
    }

    if (this.#roomId !== undefined) {
      updatedGroup.relations.roomId = this.#roomId;
    }
    if (this.#applianceIds !== undefined) {
      updatedGroup.relations.applianceIds = [...this.#applianceIds];
    }
    if (this.#hoodIds !== undefined) {
      updatedGroup.relations.hoodIds = [...this.#hoodIds];
    }
    if (this.#position !== undefined) {
      updatedGroup.airchefValues.transform.position = simpleClone(this.#position);
    }
    if (this.#rotation !== undefined) {
      updatedGroup.airchefValues.transform.rotation = simpleClone(this.#rotation);
    }

    if (this.#resetNumber) {
      updatedGroup.airchefValues.itemNumber = undefined;
    }

    if (this.#rows.length === 0) {
      throw new InvalidStateError('Group updater ended with 0 rows!');
    }
    updatedGroup.rows = this.#rows as [Row] | [Row, Row];

    return updatedGroup;
  }
}

export class RowBuilder {
  readonly #row: Row;
  #appliances: ApplianceId[];
  #floating: AppliancePositioner[];
  #hoods: HoodId[];
  #generateId: boolean = false;
  #xOffset?: Measurement<'length'>;
  #yOffset?: Measurement<'length'>;

  constructor(row?: Row) {
    this.#row = row
      ? simpleClone(row)
      : {
          id: generateRoomPartId(),
          type: 'row',
          relations: {
            applianceIds: [],
            hoodIds: [],
            floatingApplianceIds: [],
          },
          airchefValues: {
            appliancesPositionOffset: {
              x: createMeasurement(0, 'mm', 'length'),
              y: createMeasurement(0, 'mm', 'length'),
            },
          },
          haltonValues: {
            type: 'row',
            locationType: 'Island',
            additionalHoods: 0,
          },
        };

    this.#appliances = simpleClone(this.#row.relations.applianceIds);
    this.#hoods = simpleClone(this.#row.relations.hoodIds);
    this.#floating = simpleClone(this.#row.relations.floatingApplianceIds);
  }

  setGenerateId(generate: boolean): this {
    this.#generateId = generate;
    return this;
  }

  setAppliances(appliances: ApplianceId[]): this {
    this.#appliances = appliances;
    return this;
  }
  setHoods(hoods: HoodId[]): this {
    this.#hoods = hoods;
    return this;
  }
  setFloatingAppliances(floating: AppliancePositioner[]): this {
    this.#floating = floating;
    return this;
  }

  /**
   *
   * @param id appliance id to add
   * @param nextTo id of appliance already in group, add the new one next to this
   * @param beforeOrAfter should the new appliance be added after or before the existing appliance
   */
  addAppliance(id: ApplianceId, nextTo?: ApplianceId, beforeOrAfter: 'before' | 'after' = 'after'): this {
    if (!nextTo) {
      this.#appliances.push(id);
    } else {
      const nextToIndex = this.#appliances?.indexOf(nextTo);
      if (nextToIndex >= 0) {
        const newIndex = beforeOrAfter === 'after' ? nextToIndex + 1 : nextToIndex;
        this.#appliances.splice(newIndex, 0, id);
      } else {
        this.#appliances.push(id);
      }
    }

    return this;
  }

  addFloatingAppliance(id: ApplianceId, xOffsetCm = 0, yOffsetCm = 0): this {
    if (this.#floating.some((ap) => ap.applianceId === id)) return this;

    this.#floating.push({
      offset: {
        x: createMeasurement(10 * xOffsetCm, 'mm'),
        y: createMeasurement(10 * yOffsetCm, 'mm'),
      },
      applianceId: id,
    });

    return this;
  }

  removeAppliance(id: ApplianceId): this {
    this.#appliances = this.#appliances.filter((aid) => aid !== id);
    this.#floating = this.#floating.filter((ap) => ap.applianceId !== id);
    return this;
  }

  setAppliancesOffset(x: Measurement<'length'>, y?: Measurement<'length'>): this {
    this.#xOffset = x;
    this.#yOffset = y ?? createMeasurement(0, 'mm', 'length');

    return this;
  }

  build(): Row {
    if (this.#generateId) {
      this.#row.id = generateRoomPartId<'row'>();
    }

    if (this.#appliances !== undefined) {
      this.#row.relations.applianceIds = simpleClone(this.#appliances);
    }
    if (this.#hoods !== undefined) {
      this.#row.relations.hoodIds = simpleClone(this.#hoods);
    }
    if (this.#floating !== undefined) {
      this.#row.relations.floatingApplianceIds = simpleClone(this.#floating);
    }

    if (this.#xOffset !== undefined && this.#yOffset !== undefined) {
      this.#row.airchefValues.appliancesPositionOffset = {
        x: simpleClone(this.#xOffset),
        y: simpleClone(this.#yOffset),
      };
    }

    return this.#row;
  }
}

export function rowIsEmpty(row: Row) {
  return row.relations.applianceIds.length === 0;
}
